import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';

import type { BoitageDocumentKind, BoitageWindow } from '../../../../@types/Boitage';
import type { RootState } from '../../../../store/store';
import { getCompanyBoitageWindowDocument, getCompanyBoitageWindows } from '../../../../utils/api';
import { JWTAuthorization, JWTHeader } from '../../../../utils/JWTHeader';
import tailwindConfig from '../../../../utils/tailwindColors';

type SingleButtonProps = {
  label: string;
  kind: BoitageDocumentKind;
  window: BoitageWindow;
};

const buildWindowSuffix = (window: BoitageWindow): string => {
  const year = new Date(`${window.plannedDate}T00:00:00`).getFullYear();
  return `${window.plannedLabel} ${year}`;
};

const SingleDownloadButton: React.FC<SingleButtonProps> = ({ label, kind, window }) => {
  const jwt = useSelector((state: RootState) => state.auth.token);
  const company = useSelector((state: RootState) => state.auth.company);
  const [tooltip, setTooltip] = useState<{ visible: boolean; x: number; y: number }>({ visible: false, x: 0, y: 0 });

  const enabled = Boolean(window.documents?.[kind]);
  const fullLabel = `${label} — ${buildWindowSuffix(window)}`;

  const handleClick = async() => {
    if (!enabled || !company?.id || !jwt) { return; }
    try {
      const resp = await fetch(getCompanyBoitageWindowDocument(company.id, window.year, window.eventIndex, kind), {
        headers: { Authorization: JWTAuthorization(jwt) },
      });
      if (!resp.ok) { throw new Error(`${resp.status}`); }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${label.replaceAll(/\s+/g, '-')}-${window.year}-${window.eventIndex}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // silent
    }
  };

  const handleMouseMove: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (enabled) { return; }
    const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
    setTooltip({ visible: true, x: e.clientX - rect.left + 12, y: e.clientY - rect.top + 12 });
  };

  const handleMouseLeave = () => setTooltip((t) => ({ ...t, visible: false }));

  return (
    <div className="relative inline-block" onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} onMouseEnter={handleMouseMove}>
      <button
        className={`rounded px-4 py-2 text-sm text-white ${enabled ? 'bg-blue-600 hover:bg-blue-700' : 'cursor-not-allowed bg-gray-300'}`}
        disabled={!enabled}
        onClick={handleClick}
      >
        {fullLabel}
      </button>
      {!enabled && tooltip.visible && (
        <div
          className="pointer-events-none absolute z-10 rounded bg-gray-800 px-2 py-1 text-[11px] whitespace-nowrap text-white shadow-md"
          style={{ left: tooltip.x, top: tooltip.y }}
        >
          Non disponible car non configuré par<br />votre référent mutation location
        </div>
      )}
    </div>
  );
};

const TransactionBoitageModelButton: React.FC = () => {
  const jwt = useSelector((state: RootState) => state.auth.token);
  const company = useSelector((state: RootState) => state.auth.company);

  const [windows, setWindows] = useState<BoitageWindow[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const run = async() => {
      if (!company?.id || !jwt) { setIsLoading(false); return; }
      setIsLoading(true);
      try {
        const resp = await fetch(getCompanyBoitageWindows(company.id), JWTHeader(jwt));
        if (!resp.ok) { throw new Error(`${resp.status}`); }
        const data: BoitageWindow[] = await resp.json();
        setWindows(data);
      } catch {
        setWindows([]);
      } finally {
        setIsLoading(false);
      }
    };
    run();
  }, [company?.id, jwt]);

  if (isLoading) {
    return (
      <div className="flex flex-wrap gap-2">
        <Skeleton width={260} height={36} borderRadius={4} baseColor="#e2e8f0" highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}20`} />
        <Skeleton width={300} height={36} borderRadius={4} baseColor="#e2e8f0" highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}20`} />
      </div>
    );
  }

  if (windows.length === 0) {
    return (
      <p className="text-xs text-slate-500">
        Aucune fenêtre de boîtage planifiée — votre référent doit l'activer dans le planning pour pouvoir préparer les modèles associés.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {windows.map((w) => (
        <div key={`${w.year}-${w.eventIndex}`} className="flex flex-wrap items-center gap-2">
          <SingleDownloadButton label="Modèle stop pub" kind="stop-pub" window={w} />
          <SingleDownloadButton label="Modèle sans stop pub" kind="sans-stop-pub" window={w} />
        </div>
      ))}
    </div>
  );
};

export default TransactionBoitageModelButton;
